import Suspense from '@/Components/Suspense';
import { Text } from '@/Components/inputs/Text';
import { usePageStore } from '@/Components/templates/PageStore';
import { marketoUpdateSettings } from '@/graphql/mutations/Integration.mutation';
import { statusToast, syncServiceMutation } from '@/graphql/mutations/SyncService.mutation';
import { type Integration_MarketoInfo_Query, MarketoInfoQuery } from '@/graphql/queries/Integration.query';
import { SyncServiceTypeEnum } from '@/graphql/types';
import { Cached, DeleteForever, Info } from '@mui/icons-material';
import { Button, Chip, Grid, Skeleton, Stack, Switch, TextField, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import {
  type PreloadedQuery,
  type UseQueryLoaderLoadQueryOptions,
  type VariablesOf,
  usePreloadedQuery,
  useQueryLoader,
} from 'react-relay';
import { toast } from 'react-toastify';

type Props = {
  marketoInfoRef: PreloadedQuery<Integration_MarketoInfo_Query, Record<string, unknown>>;
  loadMarketoInfo: (
    variables: VariablesOf<Integration_MarketoInfo_Query>,
    options?: UseQueryLoaderLoadQueryOptions
  ) => void;
};

const HiddenString = '********';

const Marketo = (props: Props) => {
  const { marketoInfo } = usePreloadedQuery<Integration_MarketoInfo_Query>(MarketoInfoQuery, props.marketoInfoRef);

  const [state, setState] = useState({ ...marketoInfo?.settings });

  useEffect(() => {
    setState({ ...marketoInfo?.settings });
  }, [marketoInfo?.settings]);

  const updateHandler = (type: 'update' | 'reset') => {
    usePageStore.setState({ pageLoading: true });

    marketoUpdateSettings(
      {
        input: {
          munchkinId: state.munchkinId,
          clientId: state.clientId,
          formsEnabled: state.formsEnabled,
          staticListIds: state?.staticListIds ?? [],
          ...(state?.clientSecret !== null ? { setClientSecret: state.clientSecret } : {}),
          ...(type === 'reset'
            ? {
                munchkinId: null,
                clientId: null,
                setClientSecret: null,
                staticListIds: null,
              }
            : {}),
        },
      },
      {
        onCompleted: (_, errors) => {
          usePageStore.setState({ pageLoading: false });
          if (!!errors?.length) {
            toast.error(`Error updating Marketo settings. ${errors?.map((e) => e.message).join(', ')}`);
            return;
          }

          toast.success('Marketo settings updated successfully.');
          props.loadMarketoInfo({}, { fetchPolicy: 'network-only' });
        },
      }
    );
  };

  return (
    <Grid size={{ sm: 12, md: 12 }}>
      <h3>Marketo Integration </h3>
      <p>
        Copy & Paste your Marketo Munchkin Tracker ID here to enable Marketo Tracking.{' '}
        <a
          href="https://developers.marketo.com/javascript-api/lead-tracking/"
          target="_blank"
          rel="noreferrer"
          style={{ color: 'unset' }}
        >
          <Info sx={{ height: '18px', width: '18px' }} />
        </a>
      </p>
      <Text
        className="zn-txt-1"
        fullWidth
        label="Marketo Munchkin Tracker ID"
        name="marketoMunchkinTrackerId"
        placeholder="Enter Marketo Munchkin Tracker ID"
        value={state?.munchkinId ?? ''}
        onChange={({ target: { value } }) => {
          setState({ ...state, munchkinId: value });
        }}
      />
      <p>
        To enable lead tracking within Zngly. You need to create a new API client in Marketo and provide the Client ID
        and Secret. This will allow Zngly to sync leads from form submissions and marketo emails.
      </p>
      <Text
        className="zn-txt-1"
        fullWidth
        label="Marketo Api Client Id"
        name="marketoApiClientId"
        placeholder={'Enter Marketo Api Client Id'}
        value={state?.clientId ?? ''}
        onChange={({ target: { value } }) => {
          setState({ ...state, clientId: value as string });
        }}
      />
      <Text
        className="zn-txt-1"
        fullWidth
        label="Marketo Api Client Secret"
        name="marketoApiClientSecret"
        placeholder={state?.hasClientSecret ? HiddenString : 'Enter Marketo Api Client Secret'}
        disabled={!!state?.hasClientSecret}
        value={state?.hasClientSecret ? HiddenString : state?.clientSecret}
        onChange={({ target: { value } }) => {
          setState({ ...state, clientSecret: value as string });
        }}
      />
      {state?.hasClientSecret && (
        <Stack gap={1}>
          {/* Static List Ids */}
          <Stack direction="column" gap={0.5}>
            <p>
              Enter Marketo Static List Ids you wish to Sync with Zngly.{' '}
              <Tooltip title="Enter Marketo Static List Ids you wish to Sync with Zngly.">
                <a
                  href="https://developers.marketo.com/rest-api/assets/static-lists/"
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: 'unset' }}
                >
                  <Info sx={{ height: '18px', width: '18px' }} />
                </a>
              </Tooltip>
            </p>
            <TextField
              label="Static List Ids"
              disabled={state?.staticListIds?.length === 1}
              className="zn-txt-1"
              onKeyDown={(e) => {
                if (e.key !== 'Enter') {
                  return;
                }

                const target = e.target as HTMLInputElement;
                let value = target?.value ?? '';
                value = value.replace(/\s/g, '');

                if (!value?.length || Number.isNaN(Number(value)) || state?.staticListIds?.includes(value)) {
                  target.value = value.replace(/\D/g, '');
                  if (state?.staticListIds?.includes(value)) {
                    target.value = '';
                  }

                  return;
                }

                target.value = ''; // clear the input
                setState({ ...state, staticListIds: [...(state.staticListIds ?? []), value] });
              }}
              slotProps={{
                input: {
                  sx: { mb: 'unset !important' },
                },
              }}
            />

            <Stack direction="row" gap={0.5} flexWrap="wrap">
              {state?.staticListIds?.map((id) => (
                <Chip
                  key={id}
                  label={id}
                  onDelete={() => {
                    setState({ ...state, staticListIds: state?.staticListIds?.filter((i) => i !== id) });
                  }}
                />
              ))}
            </Stack>
          </Stack>

          {/* forms */}
          <Stack alignItems="center" direction="row">
            <Switch
              checked={!!state?.formsEnabled}
              onChange={() => setState({ ...state, formsEnabled: !state?.formsEnabled })}
            />
            <p
              style={{ marginBottom: 'unset', cursor: 'auto' }}
              onClick={(e) => {
                e.preventDefault();
              }}
            >
              Forms Association {state?.formsEnabled ? 'Enabled' : 'Disabled'}{' '}
              <Tooltip title="Enabling this setting will allow Zngly to associate form submissions with marketo leads.">
                <Info sx={{ height: '18px', width: '18px' }} />
              </Tooltip>
            </p>
          </Stack>
        </Stack>
      )}
      <Stack mt={1} direction="row" gap={0.5} justifyContent="start">
        {JSON.stringify(state) !== JSON.stringify(marketoInfo?.settings) && (
          <Button className="z-btn-1" onClick={() => updateHandler('update')}>
            Save
          </Button>
        )}

        {marketoInfo?.settings?.staticListIds?.length && (
          <Tooltip title="Run a manual Integration Sync" enterDelay={250}>
            <Button
              className="z-btn-2"
              endIcon={<Cached />}
              onClick={() => {
                syncServiceMutation(
                  {
                    type: SyncServiceTypeEnum.MarketoLeads,
                    forceSync: false,
                  },
                  (data, errors) => {
                    if (!!errors?.length) {
                      toast.error(`Error syncing Marketo leads. ${errors?.map((e) => e.message).join(', ')}`);
                      return;
                    }

                    const { level, message } = statusToast(data?.syncService);
                    toast?.[level as 'success' | 'error' | 'info' | 'warning']?.(message);
                  }
                );
              }}
            >
              Sync
            </Button>
          </Tooltip>
        )}
        {marketoInfo?.settings?.marketoApiActive && <Button className="z-btn-3">Connected</Button>}
        {marketoInfo?.settings?.munchkinId && (
          <Tooltip title="Reset Marketo settings" enterDelay={250}>
            <Button className="z-btn-1 " onClick={() => updateHandler('reset')} endIcon={<DeleteForever />}>
              Reset
            </Button>
          </Tooltip>
        )}
      </Stack>
    </Grid>
  );
};

const Fallback = () => {
  return (
    <Grid size={{ sm: 12, md: 12 }}>
      <h3>Marketo Integration </h3>
      <Skeleton variant="text" className="zn-sk-1" />
    </Grid>
  );
};

const MarketoWrapper = () => {
  const [marketoInfoRef, loadMarketoInfo, disposeMarketoInfo] =
    useQueryLoader<Integration_MarketoInfo_Query>(MarketoInfoQuery);

  useEffect(() => {
    loadMarketoInfo({}, { fetchPolicy: 'network-only' });

    return () => {
      disposeMarketoInfo();
    };
  }, []);

  return (
    <Suspense fallback={<Fallback />} exists={marketoInfoRef}>
      <Marketo marketoInfoRef={marketoInfoRef!} loadMarketoInfo={loadMarketoInfo} />
    </Suspense>
  );
};

export default MarketoWrapper;
